import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CreditCard, ArrowRight, BadgeCheck } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiClient } from "@/lib/api";
import { FALLBACK_LEVELS, FALLBACK_DEADLINES, CALC_SUBJECTS, PAYMENT_URL } from "./data";
import { SectionHeading } from "./Services";
import { scrollToSection } from "@/lib/scrollTo";

export const Calculator = () => {
  const [rates, setRates] = useState({ levels: FALLBACK_LEVELS, deadlines: FALLBACK_DEADLINES, min_order: 29, currency: "£" });
  const [subject, setSubject] = useState(CALC_SUBJECTS[0]);
  const [level, setLevel] = useState("undergraduate");
  const [deadline, setDeadline] = useState("7d");
  const [words, setWords] = useState(1000);

  useEffect(() => {
    apiClient.get("/rates").then((r) => {
      if (r.data?.levels?.length) setRates(r.data);
    }).catch(() => {});
  }, []);

  const { price, per250 } = useMemo(() => {
    const lvl = rates.levels.find((l) => l.value === level) || rates.levels[0];
    const dl = rates.deadlines.find((d) => d.value === deadline) || rates.deadlines[0];
    const rate250 = lvl.rate250 * dl.multiplier;
    const raw = ((words || 0) / 250) * rate250;
    return { price: Math.max(rates.min_order, Math.round(raw * 2) / 2), per250: rate250.toFixed(2) };
  }, [rates, level, deadline, words]);

  const cur = rates.currency || "£";
  const lvlLabel = rates.levels.find((l) => l.value === level)?.label;
  const dlLabel = rates.deadlines.find((d) => d.value === deadline)?.label;

  return (
    <section id="pricing" className="border-y border-[#D4AF37]/10 bg-[#F3EEE2]" data-testid="pricing-section">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-36">
        <SectionHeading id="pricing" overline="Instant Estimate" title="Transparent pricing," accent="before you commit." />
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.33, 1, 0.68, 1] }}
            className="luxury-card rounded-xl p-6 sm:p-10"
          >
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">Subject</Label>
                <Select value={subject} onValueChange={setSubject}>
                  <SelectTrigger data-testid="calc-subject-select" className="border-[#D4AF37]/25 bg-white text-[#12294D]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-[#D4AF37]/25 bg-[#F3EEE2] text-[#12294D]">
                    {CALC_SUBJECTS.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`calc-subject-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">Academic Level</Label>
                <Select value={level} onValueChange={setLevel}>
                  <SelectTrigger data-testid="calc-level-select" className="border-[#D4AF37]/25 bg-white text-[#12294D]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-[#D4AF37]/25 bg-[#F3EEE2] text-[#12294D]">
                    {rates.levels.map((l) => (
                      <SelectItem key={l.value} value={l.value} data-testid={`calc-level-${l.value}`}>
                        {l.label} — {cur}{l.rate250}/250 words
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">Deadline</Label>
                <Select value={deadline} onValueChange={setDeadline}>
                  <SelectTrigger data-testid="calc-deadline-select" className="border-[#D4AF37]/25 bg-white text-[#12294D]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-[#D4AF37]/25 bg-[#F3EEE2] text-[#12294D]">
                    {rates.deadlines.map((d) => (
                      <SelectItem key={d.value} value={d.value} data-testid={`calc-deadline-${d.value}`}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">Word Count</Label>
                <Input
                  type="number"
                  min={250}
                  step={250}
                  value={words}
                  onChange={(e) => setWords(Math.max(0, parseInt(e.target.value) || 0))}
                  data-testid="calc-words-input"
                  className="border-[#D4AF37]/25 bg-white text-[#12294D]"
                />
              </div>
            </div>
            <p className="mt-6 flex items-center gap-2 text-xs text-[#5E6E85]">
              <BadgeCheck size={14} className="shrink-0 text-[#A07E1F]" />
              Rates from {cur}7.50 per 250 words. Includes free originality report, referencing, formatting and unlimited revisions.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.33, 1, 0.68, 1] }}
            className="flex flex-col justify-between rounded-xl border border-[#D4AF37]/40 bg-white p-6 sm:p-10"
            data-testid="calc-price-panel"
          >
            <div>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#5E6E85]">Estimated price</p>
              <div className="mt-4 flex items-end gap-3">
                <motion.span
                  key={price}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="font-serif-display text-6xl font-semibold text-[#A07E1F] sm:text-7xl"
                  data-testid="calc-price-value"
                >
                  {cur}{price}
                </motion.span>
                <span className="pb-3 font-mono-accent text-xs text-[#5E6E85]">GBP · approx.</span>
              </div>
              <p className="mt-4 text-sm text-[#46586F]" data-testid="calc-price-breakdown">
                {words.toLocaleString()} words · {lvlLabel} · due in {dlLabel} ({cur}{per250} per 250 words)
              </p>
            </div>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <a
                href="/order"
                data-testid="calc-order-button"
                className="group flex flex-1 items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-6 py-4 font-mono-accent text-[11px] font-bold uppercase tracking-[0.18em] text-[#0A1E3C] transition-colors duration-300 hover:bg-[#C9A227]"
              >
                Order at This Price
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a
                href={PAYMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="calc-pay-now-button"
                className="flex flex-1 items-center justify-center gap-2 rounded-full border border-[#D4AF37]/50 px-6 py-4 font-mono-accent text-[11px] uppercase tracking-[0.18em] text-[#A07E1F] transition-colors duration-300 hover:bg-[#D4AF37]/10"
              >
                <CreditCard size={15} /> Pay Now
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
